import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ExternalLink } from 'lucide-react';
import logger from '../utils/logger';

const API = process.env.REACT_APP_BACKEND_URL;

const StorePage = () => {
  const [products, setProducts] = useState([]);
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, artistRes] = await Promise.all([
          axios.get(`${API}/api/store/products`),
          axios.get(`${API}/api/artist`)
        ]);
        setProducts(productsRes.data);
        setArtist(artistRes.data);
      } catch (e) {
        logger.error('Failed to load products', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // API and axios are stable constants/imports - safe to omit from dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shopifyUrl = artist?.shopify_url || 'https://youngblackmaninc.com';

  return (
    <div className="min-h-screen bg-[#0D0D0D] pt-24 pb-32" data-testid="store-page">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Young Black Man Inc.</p>
        <h1 className="font-['Bebas_Neue'] text-5xl md:text-6xl">MERCH STORE</h1>
        <p className="text-[#A3A3A3] mt-4 max-w-xl">
          Premium artistic streetwear. Bold, colorful, and made to make a statement.
        </p>

        {/* Shop Now CTA */}
        <a
          href={shopifyUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-3 mt-8 px-8 py-4 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors"
          data-testid="shop-now-cta"
        >
          <ExternalLink size={20} />
          Shop on Official Store
        </a>
      </div>

      {/* Products Preview */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <h2 className="font-['Bebas_Neue'] text-2xl mb-8">FEATURED ITEMS</h2>
        
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {[...Array(6)].map((_, i) => (
              <div key={`skeleton-${i}`} className="space-y-4">
                <div className="aspect-square skeleton" />
                <div className="h-4 skeleton w-3/4" />
                <div className="h-6 skeleton w-1/3" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {products.map((product, idx) => (
              <a
                key={product.id}
                href={shopifyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group animate-fade-in-up"
                style={{ animationDelay: `${idx * 0.1}s` }}
                data-testid={`product-${product.id}`}
              >
                <div className="relative aspect-square bg-[#1A1A1A] overflow-hidden">
                  {product.images?.[0] ? (
                    <img
                      src={product.images[0]}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#0A2216] to-[#0D0D0D]" />
                  )}
                  
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="px-6 py-3 bg-[#D4AF37] text-black font-medium uppercase tracking-wider flex items-center gap-2">
                      <ExternalLink size={18} />
                      View on Store
                    </span>
                  </div>
                </div>

                <div className="mt-4">
                  <h3 className="font-medium group-hover:text-[#D4AF37] transition-colors">{product.name}</h3>
                  <p className="text-[#D4AF37] font-['Bebas_Neue'] text-2xl mt-1">
                    ${product.price.toLocaleString()}
                  </p>
                  <p className="text-xs text-[#737373] uppercase tracking-wider mt-1">{product.category}</p>
                </div>
              </a>
            ))}
          </div>
        )}

        {/* Bottom CTA */}
        <div className="text-center mt-16 py-12 border-t border-white/10">
          <h3 className="font-['Bebas_Neue'] text-3xl mb-4">READY TO SHOP?</h3>
          <p className="text-[#A3A3A3] mb-8">
            Visit our official Shopify store for the full collection, sizing guides, and secure checkout.
          </p>
          <a
            href={shopifyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-8 py-4 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors"
          >
            <ExternalLink size={20} />
            Visit youngblackmaninc.com
          </a>
        </div>
      </div>
    </div>
  );
};

export default StorePage;
